/**
 * progressCopy.ts — how far through the questions a patient is, in words that
 * are encouraging AND true (CTO, 2026-09-11: "don't always show how easy it
 * is… interesting text indicators showing how much is left"; "the patient
 * should never feel like they have a lot left").
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY IT IS NOT "Question 7 of 12" ANY MORE. That count came off the SERVED
 * list, and the served list is R28-redacted: the questions that name
 * treatments are withheld until the eligibility gate passes, then released
 * part-way through. So the total GREW under the patient's feet — "Question 7
 * of 12" became "Question 9 of 14" — and the bar went backwards. A finish line
 * that moves away from someone tells them they have a lot left, which is the
 * one thing this screen was asked never to do.
 *
 * WHAT IT MEASURES INSTEAD: positions in the WHOLE question set, by `idx`.
 * Redaction removes questions but never renumbers them, so the set's last
 * `idx` is a finish line that does not move. clinic-service sends it as
 * `lastQuestionIdx`; an older clinic-service falls back to the highest `idx`
 * we can see, which is the same answer on today's set (its last question is
 * public). Progress is counted from the first question the patient answers —
 * the height and weight re-ask is submitted for them and never shown.
 *
 * WHY THAT IS HONEST AND NOT JUST KIND. A position is somewhere a question
 * COULD be. Some are skipped (a follow-up that does not apply), so the
 * positions still ahead are an UPPER BOUND on the questions still ahead, and
 * every phrase is chosen against that bound. The error can only run one way:
 * we may say "nearly there" a question late, never early.
 *
 * NUMBERS ONLY WHEN THEY ARE KNOWN. "Only 2 more after this" is a count, and a
 * count is printed only when every remaining position is a question already on
 * screen-to-be — no withheld slot, no follow-up that might yet appear.
 * Otherwise the line is a phrase. "Last question" means nothing can follow.
 *
 * Pure and DOM-free so each threshold is testable on its own
 * (`progressCopy.spec.ts`). It knows positions and nothing about answers.
 */

export interface ProgressInput {
  /** `idx` of the question on screen. */
  currentIdx: number;
  /** `idx` of the first question the patient answers (the first non-capture question served). */
  firstIdx: number;
  /** `idx` of the last question in the WHOLE set: `lastQuestionIdx`, else the highest served. */
  lastIdx: number;
  /** How many served, currently visible questions come after this one. */
  knownAfter: number;
}

export interface QuestionProgress {
  /** For the bar. Never 100 — the result screen is the finish. */
  percent: number;
  /** The words above the question. */
  line: string;
}

/** The bar's floor, so the first screen still shows a start. */
const MIN_PERCENT = 6;

/** The positions behind one question, shared by the line and the leave-step count. */
interface Measure {
  total: number;
  done: number;
  ahead: number;
  /** Every position still ahead is a question already served — no withheld slot, no pending follow-up. */
  exact: boolean;
}

function measure({ currentIdx, firstIdx, lastIdx, knownAfter }: ProgressInput): Measure {
  // Positions from the first answered question to the finish line, inclusive.
  const total = Math.max(1, lastIdx - firstIdx + 1);
  // Positions already behind the patient. Clamped, so a resume pointer outside
  // the set can neither go negative nor run past the end.
  const done = Math.min(Math.max(0, currentIdx - firstIdx), total - 1);
  // Positions still ahead of this one: an upper bound on the questions ahead.
  const ahead = Math.max(0, lastIdx - currentIdx);
  return { total, done, ahead, exact: ahead === knownAfter };
}

export function questionProgress(input: ProgressInput): QuestionProgress {
  const { total, done, ahead, exact } = measure(input);
  const percent = Math.max(MIN_PERCENT, Math.round(((done + 1) / (total + 1)) * 100));
  return { percent, line: progressLine(done, total, ahead, exact) };
}

/**
 * Every fixed phrase the line can be. A table rather than eight literals inline,
 * for one reason: `PROGRESS_LINE_PATTERN` below is built from it, so a new phrase
 * cannot be added without the pattern following.
 */
const PHRASE = {
  last: 'Last question',
  oneMore: 'Just one more after this',
  start: "Let's get started",
  early: 'Off to a good start',
  going: 'Making good progress',
  half: 'Halfway there',
  past: 'Over halfway there',
  nearly: 'Nearly there',
} as const;

/** The one parametric line, and the only place its shape is written. */
const someMore = (ahead: number): string => `Only ${ahead} more after this`;

/**
 * Matches ANY line this module can produce — "the questionnaire frame's eyebrow,
 * whatever it currently says".
 *
 * ⚠️ EXPORTED FOR `testing/journeySteps.ts`, AND THAT IS NOT A CONVENIENCE. Its
 * `continueFromProjectionToQuestions` waits for the questionnaire step to SETTLE,
 * and it used to do that by matching `/Question \d+ of \d+/` — the copy this
 * module replaced. With a phrase there instead, twelve specs across nine files sat
 * waiting for a frame that was already on screen and then reported a broken
 * journey. Matching the closed SET, built from the table above, is the version of
 * that helper's stated intent that cannot rot: add a phrase and the pattern has it.
 */
export const PROGRESS_LINE_PATTERN = new RegExp(
  `^(?:${[...Object.values(PHRASE), String.raw`Only \d+ more after this`]
    .map((phrase) => phrase.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`))
    .join('|')})$`,
);

/** The most positions ahead the line will put a number on ("Only 3 more after this"). */
const MAX_COUNTED_AHEAD = 3;
/** From here on the line says "Nearly there" — the same point the leave-step dialog may say it. */
const NEARLY_THROUGH = 0.75;

function progressLine(done: number, total: number, ahead: number, exact: boolean): string {
  if (ahead === 0) return PHRASE.last;
  if (exact && ahead === 1) return PHRASE.oneMore;
  if (exact && ahead <= MAX_COUNTED_AHEAD) return someMore(ahead);
  if (done === 0) return PHRASE.start;
  const through = done / total;
  if (through < 0.25) return PHRASE.early;
  if (through < 0.5) return PHRASE.going;
  if (through === 0.5) return PHRASE.half;
  if (through < NEARLY_THROUGH) return PHRASE.past;
  return PHRASE.nearly;
}

/* ── The leave-step dialog (CTO, 2026-09-25) ─────────────────────────────── */

/**
 * The length of the WHOLE check, as the journey already promises it on Step 0
 * ("a 2-minute check, reviewed by a UK clinician"). Step 0 and the entry loader
 * card's first headline read it through `serviceClaims.ts` (`TWO_MINUTE_CHECK`
 * is built from this number, never typed).
 * One number, so the leave-step dialog can never promise a different one — and
 * it is only ever the WHOLE check's length: the dialog quotes it where it does
 * not know how far through the patient is, never as the time the rest takes.
 */
export const CHECK_MINUTES = 2;

/**
 * How much is left, for the "Leave this step?" moment (navigation/
 * LeaveConfirmDialog.tsx, journey variant). CTO, 2026-09-25: *"instead of this,
 * 'you're a couple of steps away' (maybe it only takes around 2 minutes)…"*.
 *
 * THE SAME RULES AS THE LINE, SO THE DIALOG NEVER CONTRADICTS THE PAGE BEHIND
 * IT. `count` is printed only where the line itself would print a number (every
 * remaining position is a question already served, and no more than
 * {@link MAX_COUNTED_AHEAD} of them after this one) — and it counts the question
 * on screen too, because the patient has not answered it yet: on the last
 * question the line says "Last question" and the dialog says "Just 1 question
 * left". `nearlyThere` is true exactly where the line has reached a count or
 * "Nearly there" — never earlier, so the dialog cannot say "nearly there" a
 * question before the page does.
 */
export interface RemainingQuestions {
  /** Questions still to answer, the one on screen included — or null where no honest number exists. */
  count: number | null;
  /** The page's own line is a count, "Last question" or "Nearly there". */
  nearlyThere: boolean;
}

export function remainingQuestions(input: ProgressInput): RemainingQuestions {
  const { total, done, ahead, exact } = measure(input);
  const count = exact && ahead <= MAX_COUNTED_AHEAD ? ahead + 1 : null;
  return { count, nearlyThere: count !== null || done / total >= NEARLY_THROUGH };
}

/** The dialog's heading and its one line. */
export interface LeaveStepCopy {
  heading: string;
  line: string;
}

export const LEAVE_STEP_NEARLY = 'You’re nearly there';
export const LEAVE_STEP_ON_YOUR_WAY = 'You’re on your way';

/**
 * The words, from what is known — never a number that is not. `null` is "we do
 * not know where they are" (Step 0, before the first question; the leading
 * height/weight re-ask), and gets the one claim that is true everywhere: the
 * length of the whole check. Early in the questions it is the same line, under a
 * heading that does not pretend the end is close.
 *
 * NO MINUTES ONCE THE END IS NEAR (review, 2026-09-25). The count and the
 * "nearly there" lines used to add "it only takes about 2 minutes to finish" /
 * "a couple of minutes". That is the whole check's length: on q15, with the
 * agreement ticked and only Continue left, the dialog told the patient the last
 * question takes as long as Step 0 says all of them do — the count true, the
 * time four-fold too long, and "a lot left" is the one feeling this module is
 * here to prevent. Late in the check the real count, or "most of it is done",
 * is the encouraging thing, and it is true.
 */
export function leaveStepCopy(remaining: RemainingQuestions | null): LeaveStepCopy {
  if (remaining?.count != null) {
    const questions = remaining.count === 1 ? '1 question' : `${remaining.count} questions`;
    return { heading: LEAVE_STEP_NEARLY, line: `Just ${questions} left to finish your check.` };
  }
  if (remaining?.nearlyThere) {
    // `nearlyThere` without a count means at least NEARLY_THROUGH of the
    // positions are behind the patient — "most" is exactly what is known.
    return { heading: LEAVE_STEP_NEARLY, line: 'Most of your check is already done.' };
  }
  return {
    heading: LEAVE_STEP_ON_YOUR_WAY,
    line: `The whole check only takes about ${CHECK_MINUTES} minutes to finish.`,
  };
}
